import { useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import axios, { HttpStatusCode } from 'axios';
// Helpers
import { countPdfPages } from 'helpers/countPdfPages';
import { generateDownloadAnalyticsData } from 'helpers/generateDownloadAnalyticsData';
import { getFileKeyFromAWSLink } from 'helpers/getFileKeyFromAWSLink';
// Hooks
import { useLocaleNavigate } from 'hooks/useLocaleNavigate';

// Services
import { Analytics } from 'services/analytics';

import { sendAnalyticEvent } from 'data/actions/analytics';
// Actions
import {
  chargeCreditInEditor,
  convertDocument,
  convertExistingDocument,
  downloadDocument,
  getUploadLink,
  setConvertDocumentData,
  updateDownloadProgress
} from 'data/actions/documents';
import { toggleModal } from 'data/actions/modals';
// import { getUserBalance } from 'data/actions/user';
// Selectors
import { editDocumentDataSelector } from 'data/selectors/documents';
import {
  isUserAuthenticated,
  userBalanceSelector,
  userEmailSelector,
  userSubscriptionSelector
} from 'data/selectors/user';

import { downloadByUrl } from 'utils/downloadByUrl';
import { formatFileSize } from 'utils/formatFileSize';
import { getPrevPage } from 'utils/prevPath';

import { LOCAL_STORAGE_KEYS } from 'ts/constants/local-storage-keys';
// Constants
import { PAGE_LINKS } from 'ts/constants/page-links';
import { EModalTypes } from 'ts/enums/modal.types';
import type { IConvertDocumentDto } from 'ts/interfaces/documents/document';
import type { IService } from 'ts/interfaces/services/service';
import { EServiceType } from 'ts/interfaces/services/service';
import { getFileTypeByName, InternalFileType } from 'ts/types/file';

const useFileUploadAndConvert = ({ service, modalType }: { service?: IService; modalType?: EModalTypes }) => {
  const navigate = useLocaleNavigate();
  const dispatch = useDispatch();
  const isAuth = useSelector(isUserAuthenticated);
  const userSubscription = useSelector(userSubscriptionSelector);
  const email = useSelector(userEmailSelector);
  const editDocumentData = useSelector(editDocumentDataSelector());
  const userBalance = useSelector(userBalanceSelector);

  const isFlowStoppedRef = useRef(false);

  // Balance is ensured globally; avoid fetching here to prevent duplicate requests during tools list render

  const handlePaymentNavigate = () => {
    if (userBalance === '0' || userBalance === null || userBalance === undefined) {
      navigate(PAGE_LINKS.PAYMENT);
    }
  };

  const analyticsEventUpload = ({
    success,
    size,
    errorCode,
    pagesCount,
    from
  }: {
    success: boolean;
    size: number;
    from: string;
    errorCode?: number;
    pagesCount?: number;
  }) => {
    void Analytics.sendEvent({
      event: 'file_upload_status',
      data: {
        status: success ? 'success' : 'fail',
        place: 'additional',
        errorCode,
        size: formatFileSize(size),
        accurate_size: size / 1000000,
        pages_counter: pagesCount,
        file_format: `.${from?.toLowerCase() || '.pdf'}`,
        is_validation_error: success ? 'false' : 'true'
      }
    });
  };

  const handleConvertDocument = (dataToConvert: IConvertDocumentDto, signUp?: boolean, subscription?: any) => {
    if (!userSubscription && !subscription?.id) {
      dispatch(updateDownloadProgress(100));
      return setTimeout(
        () => {
          if (service?.serviceType !== EServiceType.CONVERTOR && editDocumentData?.id) {
            dispatch(
              convertExistingDocument({
                from: dataToConvert.from,
                to: dataToConvert.to,
                fileId: editDocumentData.id,
                onSuccess: () => {
                  handlePaymentNavigate();
                },
                onFailed: (error: any) => {
                  if (error?.response?.status === HttpStatusCode.PaymentRequired) {
                    handlePaymentNavigate();
                    return;
                  }

                  dispatch(
                    toggleModal({
                      type: EModalTypes.FILE_UPLOAD_ERROR,
                      visible: true
                    })
                  );
                }
              } as any)
            );
          } else {
            dispatch(
              convertDocument(
                dataToConvert,
                (res: any) => {
                  if (
                    getPrevPage()?.includes('merge-pdf') ||
                    getPrevPage()?.includes('image-to-pdf') ||
                    getPrevPage()?.includes('pdf-converter')
                  ) {
                    dispatch(chargeCreditInEditor({ size: res?.size, filename: res?.filename, id: res?.id }));
                  }
                  handlePaymentNavigate();
                },
                (error: any) => {
                  Analytics.sendEvent({
                    event: 'file_upload_status',
                    data: {
                      status: 'fail',
                      place: 'additional',
                      errorCode: error?.response?.status,
                      size: formatFileSize(dataToConvert.size),
                      accurate_size: dataToConvert.size / 1000000,
                      file_format: dataToConvert.from || dataToConvert.to,
                      is_validation_error: 'false'
                    }
                  });
                  if (error?.response?.status === HttpStatusCode.PaymentRequired) {
                    handlePaymentNavigate();
                    return;
                  }

                  dispatch(
                    toggleModal({
                      type: EModalTypes.FILE_UPLOAD_ERROR,
                      visible: true
                    })
                  );
                }
              )
            );
          }
        },
        signUp ? 0 : 1000
      );
    }

    const onSuccess = (res: any) => {
      if (dataToConvert.from === 'PDF' && dataToConvert.to === 'PDF' && res.id) {
        dispatch(updateDownloadProgress(100));
        setTimeout(() => {
          dispatch(
            downloadDocument(
              res.id,
              () => {},
              () => {
                dispatch(
                  sendAnalyticEvent({
                    event: 'file_download_status',
                    data: generateDownloadAnalyticsData({
                      success: true,
                      isManual: true,
                      fileData: res,
                      inputFile: {
                        size: dataToConvert.size || editDocumentData?.size
                      }
                    })
                  })
                );
              }
            )
          );
          dispatch(toggleModal({ visible: false }));
        }, 500);
        return;
      }
    };

    const onFailed = (error: any) => {
      Analytics.sendEvent({
        event: 'file_upload_status',
        data: {
          status: 'fail',
          place: 'additional',
          errorCode: error?.response?.status,
          size: formatFileSize(dataToConvert.size),
          accurate_size: dataToConvert.size / 1000000,
          file_format: dataToConvert.from || dataToConvert.to,
          is_validation_error: 'false'
        }
      });

      if (error?.response?.data?.message === 'error.convert.unsupported-formats') {
        dispatch(toggleModal({ visible: false }));
        dispatch(updateDownloadProgress(0));
      }

      if (error?.response?.status === HttpStatusCode.PaymentRequired) {
        dispatch(toggleModal({ visible: false }));
        dispatch(updateDownloadProgress(0));
        handlePaymentNavigate();
        window.scrollTo(0, 0); // Scrolls to the top of the page
        return;
      }

      dispatch(toggleModal({ type: EModalTypes.FILE_UPLOAD_ERROR, visible: true }));
      dispatch(updateDownloadProgress(0));
    };

    if (service?.serviceType !== EServiceType.CONVERTOR && editDocumentData?.id) {
      dispatch(
        convertExistingDocument({
          from: dataToConvert.from,
          to: dataToConvert.to,
          fileId: editDocumentData.id,
          onSuccess,
          onFailed
        })
      );
    } else {
      dispatch(convertDocument(dataToConvert, onSuccess, onFailed));
    }
  };
  const handleConvertFile = (dataToConvert: IConvertDocumentDto) => {
    dispatch(sendAnalyticEvent({ event: 'converting_with_progress_view' }));
    if (!isAuth) {
      setTimeout(() => {
        dispatch(updateDownloadProgress(100));
      }, 500);
      return setTimeout(() => {
        dispatch(
          toggleModal({
            type: EModalTypes.ENTER_EMAIL_ADDRESS,
            visible: true,
            options: {
              handleConvertDocument: (subscription: any) => handleConvertDocument(dataToConvert, true, subscription),
              signUp: true,
              servicePath: service?.path
            }
          })
        );
      }, 1000);
    }
    handleConvertDocument(dataToConvert);
  };

  const handleUploadFileByLinkToS3 = async (file: File, uploadLink: string, to?: string) => {
    dispatch(sendAnalyticEvent({ event: 'upload_link_received' }));
    try {
      const from: InternalFileType =
        getFileTypeByName(file?.name) ||
        (Array.isArray(service?.from) ? service?.from[0] : service?.from) ||
        InternalFileType.PDF;

      const getModalType = () => {
        if (modalType) return modalType;

        if (from === 'PDF' && to === 'PDF') return EModalTypes.PROGRESS_EDIT_FILE;
        return EModalTypes.PROGRESS_EDIT_FILE;
      };

      const secondaryType = from === 'PDF' ? to || service?.to || 'PNG' : from;

      const res = await axios.put(uploadLink, file);

      const pagesCount = from === 'PDF' ? await countPdfPages(file) : 1;

      const dataToConvert: IConvertDocumentDto = {
        filename: file.name,
        size: file.size,
        key: getFileKeyFromAWSLink(res?.request?.responseURL),
        url: res?.request?.responseURL,
        pagesCount,
        from,
        to: to || (Array.isArray(service?.to) ? service?.to[0] : service?.to) || InternalFileType.PNG,
        serviceType: service?.serviceType
      };

      dispatch(updateDownloadProgress(0));
      dispatch(
        toggleModal({
          type: getModalType() as EModalTypes,
          visible: true,
          options: {
            file,
            secondaryType,
            dataToConvert,
            onUserClose: () => (isFlowStoppedRef.current = true)
          }
        })
      );
      dispatch(
        sendAnalyticEvent({
          event: 'upload_file_to_bucket',
          data: {
            status: 'success',
            filename: file?.name
          }
        })
      );
      // awaiting the downloading animation
      setTimeout(() => {
        if (isFlowStoppedRef.current) return;

        dispatch(setConvertDocumentData(dataToConvert));

        // set converted data to localStorage and use this data for google auth
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.DATA_TO_CONVERT,
          JSON.stringify({
            file: dataToConvert,
            service: service?.path,
            email: email,
            serviceType: service?.serviceType
          })
        );

        analyticsEventUpload({
          size: file?.size,
          success: true,
          from,
          pagesCount
        });

        if (
          (getPrevPage()?.includes('merge-pdf') || getPrevPage()?.includes('image-to-pdf')) &&
          Number(userBalance) > 0 &&
          isAuth
        ) {
          dispatch(
            convertDocument(
              dataToConvert,
              (res: any) => {
                dispatch(chargeCreditInEditor({ size: res?.size, filename: res?.filename, id: res?.id }));
              },
              () => {}
            )
          );
          downloadByUrl({ url: uploadLink, filename: file.name });

          dispatch(toggleModal({ visible: false }));
          return;
        }

        handleConvertFile(dataToConvert);
      }, 3000);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  // upload to s3 bucket and convert file
  const handleUploadFile = (file: File, to?: string) => {
    if (!file) return;

    dispatch(
      sendAnalyticEvent({
        event: 'file_from_provider_chosen',
        data: {
          features_name: service?.path?.replace('/', '') || '',
          method: 'click'
        }
      })
    );

    dispatch(
      getUploadLink({
        filename: file?.name,
        onSuccess: (res: any) => {
          handleUploadFileByLinkToS3(file, res[0]?.url, to);
        },
        service
      })
    );
  };

  return {
    handleUploadFile,
    handleConvertDocument
  };
};

export default useFileUploadAndConvert;
